/**
 * Where the next reply runs, as the browser can tell from the state it has (the hub decides; this
 * is what the page says before anyone types): an online machine with Claude Code, a stopped cloud
 * VM Orgus starts for the reply, or nothing yet, with the one step to take.
 */
import { startsClaudeCode } from '@orgus/protocol';
import type { CloudState, Machine } from '@orgus/protocol';

export type RunnerStatus =
  | { kind: 'ready'; machine: string; others: number }
  | { kind: 'wake'; vm: string }
  | { kind: 'starting'; vm: string }
  | { kind: 'none' };

export function runnerStatus(machines: readonly Machine[], cloud: CloudState | null, chosen: string | null = null): RunnerStatus {
  const ok = machines.filter((m) => m.online && startsClaudeCode(m) && (!chosen || m.id === chosen));
  if (ok.length) {
    const first = ok.find((m) => m.local) ?? [...ok].sort((a, b) => a.load - b.load)[0]!;
    return { kind: 'ready', machine: first.name, others: ok.length - 1 };
  }
  const vms = (cloud?.vms ?? []).filter((v) => v.machineId && (!chosen || v.machineId === chosen));
  const starting = vms.find((v) => v.state === 'pending' || v.op?.kind === 'start');
  if (starting) return { kind: 'starting', vm: starting.name };
  const stopped = vms.find((v) => v.state === 'stopped' && !v.op);
  if (stopped) return { kind: 'wake', vm: stopped.name };
  return { kind: 'none' };
}

/** One line for the strip above the composer. */
export function runnerLine(s: RunnerStatus): string {
  switch (s.kind) {
    case 'ready':
      return `Replies run on ${s.machine} as Claude Code${s.others ? `, or ${s.others} other ${s.others === 1 ? 'machine' : 'machines'}` : ''}.`;
    case 'starting':
      return `${s.vm} is starting. Replies run there in about a minute.`;
    case 'wake':
      return `No machine is online. Orgus starts ${s.vm} when you send, about a minute.`;
    case 'none':
      return 'No machine is online to run replies. Add a machine or a cloud VM to start.';
  }
}

/** Demo mode only: ?runner=none or ?runner=wake shows those states (screenshots and reviews). */
export function demoRunner(mock: boolean): RunnerStatus | null {
  if (!mock || typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search).get('runner');
  return q === 'none' ? { kind: 'none' } : q === 'wake' ? { kind: 'wake', vm: 'swift-otter' } : null;
}
